import type { ReactNode } from "react";
import type { ImageSourcePropType } from "react-native";
import { Image, Text, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { DocumentRow as MoonDocumentRow } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import StatusBadge from "../../components/StatusBadge";
import { useTranslations } from "@src/packages/i18n";

export type DocumentRowStatus = "on-file" | "requested" | "missing";
export type DocumentRowBadge = {
  label: string;
  tone?: "neutral" | "gold" | "coral" | "green";
};
export type DocumentTestResultCategory =
  "low" | "normal" | "high" | "borderline";

export const documentTestTubesIcon =
  require("@src/apps/moon/assets/images/you-icons/test-tubes.png") as number;

const RANGE_GRADIENT = [
  "#D88560",
  "#E0A968",
  "#7BAF85",
  "#E0A968",
  "#D88560",
] as const;

const CATEGORY_POSITION: Record<DocumentTestResultCategory, `${number}%`> = {
  low: "15%",
  borderline: "30%",
  normal: "50%",
  high: "85%",
};

function getStatusBadge(
  status: DocumentRowStatus,
  t: (path: string) => string,
) {
  if (status === "on-file") {
    return null;
  }
  if (status === "requested") {
    return { label: t("documents.status.requested"), tone: "gold" as const };
  }
  return { label: t("documents.status.missing"), tone: "coral" as const };
}

export function DocumentRow({
  title,
  subtitle,
  dateLabel,
  imageSource,
  icon,
  status,
  badge,
  trailing,
  onPress,
  showChevron,
}: {
  title: string;
  subtitle?: string;
  dateLabel?: string;
  imageSource?: ImageSourcePropType;
  icon?: ReactNode;
  status?: DocumentRowStatus;
  badge?: DocumentRowBadge;
  trailing?: ReactNode;
  onPress?: () => void;
  showChevron?: boolean;
}) {
  const t = useTranslations();
  const statusBadge = badge ?? (status ? getStatusBadge(status, t) : null);
  const shouldShowChevron = showChevron ?? Boolean(onPress);

  return (
    <MoonDocumentRow
      leadingMeta={dateLabel}
      leading={
        imageSource ? (
          <Image
            source={imageSource}
            resizeMode="contain"
            style={{ width: 32, height: 32 }}
          />
        ) : (
          icon
        )
      }
      title={title}
      subtitle={subtitle}
      titleNumberOfLines={1}
      subtitleNumberOfLines={2}
      titleRight={
        statusBadge ? (
          <StatusBadge label={statusBadge.label} tone={statusBadge.tone} />
        ) : null
      }
      trailing={trailing}
      onPress={onPress}
      showChevron={shouldShowChevron}
    />
  );
}

function DocumentResultRange({
  category,
  value,
  unit,
  pending,
  pendingOpacity = 1,
}: {
  category: DocumentTestResultCategory;
  value: string;
  unit?: string;
  pending?: boolean;
  pendingOpacity?: number;
}) {
  const left = CATEGORY_POSITION[category] ?? CATEGORY_POSITION.normal;
  const showValue = !pending && value !== "See details";

  return (
    <View
      style={{
        width: 68,
        paddingTop: 18,
        marginRight: 2,
      }}
    >
      <View style={{ height: 14, justifyContent: "center" }}>
        <LinearGradient
          colors={pending ? ["#D8CDC4", "#BDAEA2", "#D8CDC4"] : RANGE_GRADIENT}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          locations={pending ? [0, 0.5, 1] : [0, 0.2, 0.5, 0.8, 1]}
          style={{
            height: 4,
            borderRadius: 2,
            opacity: pending ? pendingOpacity : 1,
          }}
        />
        {!pending ? (
          <View
            style={{
              position: "absolute",
              left,
              top: 0,
              width: 2,
              height: 14,
              borderRadius: 1,
              backgroundColor: EMBER.colors.text,
              marginLeft: -1,
            }}
          />
        ) : null}
        {showValue ? (
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: -18,
              left,
              width: 58,
              marginLeft: -29,
              alignItems: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              numberOfLines={1}
              style={{
                fontSize: 12,
                fontWeight: "700",
                color: EMBER.colors.text,
              }}
            >
              {value}
              {unit ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 9,
                    fontWeight: "500",
                    color: EMBER.colors.textLight,
                  }}
                >
                  {" "}
                  {unit}
                </Text>
              ) : null}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

export function DocumentTestResultRow({
  title,
  subtitle,
  dateLabel,
  value,
  unit,
  category,
  imageSource = documentTestTubesIcon,
  badge,
  pending,
  pendingOpacity,
  trailingAction,
  onPress,
}: {
  title: string;
  subtitle?: string;
  dateLabel?: string;
  value: string;
  unit?: string;
  category: DocumentTestResultCategory;
  imageSource?: ImageSourcePropType;
  badge?: DocumentRowBadge;
  pending?: boolean;
  pendingOpacity?: number;
  trailingAction?: ReactNode;
  onPress?: () => void;
}) {
  return (
    <DocumentRow
      title={title}
      subtitle={subtitle}
      dateLabel={dateLabel}
      imageSource={imageSource}
      badge={badge}
      trailing={
        trailingAction ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <DocumentResultRange
              category={category}
              value={value}
              unit={unit}
              pending={pending}
              pendingOpacity={pendingOpacity}
            />
            {trailingAction}
          </View>
        ) : (
          <DocumentResultRange
            category={category}
            value={value}
            unit={unit}
            pending={pending}
            pendingOpacity={pendingOpacity}
          />
        )
      }
      onPress={onPress}
    />
  );
}

export default DocumentRow;
